import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { ReactNode } from "react";
import Draggable from "react-draggable";
import { useNavigate } from "react-router-dom";

import HeadphonesIcon from "@mui/icons-material/Headphones";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppSelector } from "app/hooks";
import { NEON_NOW_INTEGRATION } from "constants/feature-flags";
import { selectCurrentClinic } from "features/auth/auth-slice";
import WidgetButton from "features/medical-scribe/components/widget-button.component";
import {
  DRAWER_MOBILE_WIDTH,
  DRAWER_WIDTH
} from "features/patients/patient-drawer";
import { viteEnv } from "utils/vite-env.util";

import IncomingCallWidget from "./incoming-call-widget.component";
import NeonNowDrawer from "./neon-now-drawer.component";

const NEON_ORIGIN = viteEnv.VITE_NEON_ORIGIN as string;
const ENV_VENDOR_APP_ID = viteEnv.VITE_VENDOR_APP_ID as string;
const ENV_CRM_APP_ID = viteEnv.VITE_CRM_APP_ID as string;

export const NEON_NOW_EVENT = {
  READY: "READY",
  INIT: "INIT",
  DIAL_OUTBOUND_NUMBER: "DIAL_OUTBOUND_NUMBER",
  CONNECT_CONTACT_STATE_CHANGE: "CONNECT_CONTACT_STATE_CHANGE",
  ACTION_RESPONSE: "ACTION_RESPONSE"
};

export function useNeonNowProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const rightOffset = useMemo(() => {
    if (!isOpen) return 0;
    if (isMobile) {
      return DRAWER_MOBILE_WIDTH;
    }

    return DRAWER_WIDTH;
  }, [isMobile, isOpen]);

  return {
    isOpen,
    handleOpen,
    handleClose,
    rightOffset,
    iframeRef
  };
}

export const NeonNowContext = createContext<{
  isOpen: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  rightOffset: number;
  sendAction: (name: string, body?: any) => void;
  sendActionWithResponse: (name: string, body?: any) => void;
  onEvent: (name: string, handler: (payload: any) => void) => () => void;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  inited: boolean;
} | null>(null);

type PatientDrawerProviderProps = {
  children?: ReactNode;
};

export const NeonNowProvider = ({ children }: PatientDrawerProviderProps) => {
  const flags = useFlags();
  const isNeonNowEnabled = flags[NEON_NOW_INTEGRATION];
  const navigate = useNavigate();
  const theme = useTheme();
  const { isOpen, handleOpen, handleClose, rightOffset, iframeRef } =
    useNeonNowProvider();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [token] = useState(() => crypto.randomUUID());
  const [inited, setInited] = useState(false);
  const neonOriginUrl = useMemo(
    () => currentClinic?.neon_origin_url || NEON_ORIGIN,
    [currentClinic?.neon_origin_url]
  );
  const neonVendorAppId =
    currentClinic?.neon_vendor_app_id || ENV_VENDOR_APP_ID;
  const neonCrmAppId = currentClinic?.neon_crm_app_id || ENV_CRM_APP_ID;

  const subs = useRef(new Map<string, Set<(p: any) => void>>());
  const actionMap = useRef(new Map<string, string>());

  const log = useCallback((...args: any[]) => {
    console.log("CRM: ", ...args);
  }, []);

  const onEvent = useCallback((name: string, handler: (p: any) => void) => {
    if (!subs.current.has(name)) subs.current.set(name, new Set());
    subs.current.get(name)!.add(handler);
    return () => {
      subs.current.get(name)!.delete(handler);
    };
  }, []);

  const sendAction = useCallback(
    (name: string, body: any = {}) => {
      if (!inited) return;
      iframeRef.current?.contentWindow?.postMessage(
        { name, payload: { ...body, token } },
        neonOriginUrl
      );
    },
    [iframeRef, inited, neonOriginUrl, token]
  );

  const sendActionWithResponse = useCallback(
    (name: string, body: any = {}) => {
      if (!inited) return;
      const actionId = crypto.randomUUID();
      actionMap.current.set(actionId, name);

      iframeRef.current?.contentWindow?.postMessage(
        { name, payload: { ...body, token, actionId } },
        neonOriginUrl
      );
    },
    [iframeRef, inited, neonOriginUrl, token]
  );

  useEffect(() => {
    const handle = (e: MessageEvent) => {
      if (e.origin !== neonOriginUrl) return;
      const { name, payload } = e.data as any;

      // Validate token for security
      if (payload?.token !== token && payload?.source !== neonVendorAppId) {
        return;
      }

      log("Received event", { name, payload });

      // Handle READY event for handshake
      if (
        name === NEON_NOW_EVENT.READY &&
        payload?.source === neonVendorAppId
      ) {
        if (!inited) {
          log("Received handshake READY event, sending INIT");
          iframeRef.current?.contentWindow?.postMessage(
            {
              name: NEON_NOW_EVENT.INIT,
              payload: { source: neonCrmAppId, token }
            },
            neonOriginUrl
          );
          setInited(true);
        }
      }

      // Handle ACTION_RESPONSE
      if (name === NEON_NOW_EVENT.ACTION_RESPONSE) {
        const { actionId, status } = payload;

        if (!actionMap.current.has(actionId)) {
          log("Received action response for unknown action.");
          return;
        }

        log(
          `Action ${actionMap.current.get(actionId)} (${actionId}) ${
            status === "SUCCESS" ? "was successful" : "failed"
          }`
        );

        // Clean up action mapping
        actionMap.current.delete(actionId);
      }

      // Notify event subscribers
      subs.current.get(name)?.forEach((fn: any) => fn(payload));
    };

    log("Waiting for NN module READY event to begin handshake.");
    window.addEventListener("message", handle);
    return () => window.removeEventListener("message", handle);
  }, [
    iframeRef,
    inited,
    token,
    log,
    neonOriginUrl,
    neonCrmAppId,
    neonVendorAppId
  ]);

  // 5️⃣ Build and provide context value
  const contextValue = useMemo(
    () => ({
      isOpen,
      handleOpen,
      handleClose,
      rightOffset,
      sendAction,
      sendActionWithResponse,
      onEvent,
      iframeRef,
      inited
    }),
    [
      isOpen,
      handleOpen,
      handleClose,
      rightOffset,
      sendAction,
      sendActionWithResponse,
      onEvent,
      iframeRef,
      inited
    ]
  );

  return (
    <NeonNowContext.Provider value={contextValue}>
      {children}
      {isNeonNowEnabled && (
        <>
          <NeonNowDrawer />
          <IncomingCallWidget
            onOpenPhoneWidget={handleOpen}
            onNavigateToPatient={(patientId) => {
              navigate(`/patients/${patientId}`);
            }}
          />
          {!isOpen && (
            <Draggable>
              <div
                style={{
                  position: "absolute",
                  right: 100,
                  bottom: 20,
                  zIndex: theme.zIndex.snackbar + 1
                }}
              >
                <WidgetButton
                  tooltip="Open Neon Now Panel"
                  startIcon={<HeadphonesIcon />}
                  onClick={handleOpen}
                  sx={{
                    background: "hsla(273, 21%, 54%, 0.85)",
                    "&:hover": {
                      background: "hsla(273, 21%, 54%, 1)"
                    },
                    color: "white",
                    borderRadius: "8px",
                    boxShadow: theme.shadows[3],
                    p: 2,
                    py: 1.5,
                    userSelect: "none"
                  }}
                />
              </div>
            </Draggable>
          )}
        </>
      )}
    </NeonNowContext.Provider>
  );
};

export const useNeonNow = () => {
  const context = useContext(NeonNowContext);
  if (!context) {
    throw new Error(`useNeonNow must be used within a NeonNowProvider`);
  }

  return context;
};
